/**
 * The languages the web app speaks, and which one a page starts in.
 *
 * Kept free of i18next so the startup script can start loading the first
 * screen's catalog before the i18n setup (in the startup chunk) arrives.
 */
import type { ResourceKey } from 'i18next';

export const SUPPORTED_LANGUAGES = ['en', 'de'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];
export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

export function isSupportedLanguage(lang: unknown): lang is SupportedLanguage {
  return typeof lang === 'string' && (SUPPORTED_LANGUAGES as readonly string[]).includes(lang);
}

export function normalizeLanguage(value: unknown): SupportedLanguage | null {
  if (typeof value !== 'string') return null;

  const normalized = value.trim().toLowerCase();
  if (isSupportedLanguage(normalized)) return normalized;

  const [base] = normalized.split('-');
  return isSupportedLanguage(base) ? base : null;
}

export function getBrowserDefaultLanguage(): SupportedLanguage {
  if (typeof navigator === 'undefined') return DEFAULT_LANGUAGE;

  const preferredLanguage =
    (Array.isArray(navigator.languages) ? navigator.languages[0] : undefined) || navigator.language;

  return normalizeLanguage(preferredLanguage) === 'de' ? 'de' : DEFAULT_LANGUAGE;
}

export interface StartupCatalog {
  readonly default: ResourceKey;
  /** True when the module holds the whole catalog (tests, packaged tools). */
  readonly complete: boolean;
}

// English is bundled with the startup code (index.ts).
const STARTUP_CATALOG_LOADERS: Record<
  Exclude<SupportedLanguage, 'en'>,
  () => Promise<StartupCatalog>
> = {
  de: () => import('virtual:rudi-startup-catalog/de'),
};

const startupCatalogs = new Map<SupportedLanguage, Promise<StartupCatalog>>();

/**
 * Load the startup keys of a language other than English, the part of its
 * catalog the first screen needs (see startupCatalogPlugin.ts).
 */
export function loadStartupCatalog(
  language: Exclude<SupportedLanguage, 'en'>
): Promise<StartupCatalog> {
  let pending = startupCatalogs.get(language);
  if (!pending) {
    pending = STARTUP_CATALOG_LOADERS[language]();
    startupCatalogs.set(language, pending);
    // A failed import may succeed on a later attempt.
    pending.catch(() => startupCatalogs.delete(language));
  }
  return pending;
}

/** The stored user's saved language, read before the session is checked. */
function storedProfileLanguage(): SupportedLanguage | null {
  try {
    const stored: unknown = JSON.parse(localStorage.getItem('rudi_auth_tokens') ?? 'null');
    const language = (stored as { user?: { profile?: { language?: unknown } | null } } | null)?.user
      ?.profile?.language;
    return normalizeLanguage(language);
  } catch {
    return null;
  }
}

/** The language the first screen renders in: the saved one, else the browser's. */
export function getInitialLanguage(): SupportedLanguage {
  return storedProfileLanguage() ?? getBrowserDefaultLanguage();
}

/** Start loading the first screen's catalog when it is not English. Never rejects. */
export function preloadInitialLanguageCatalogs(): void {
  const language = getInitialLanguage();
  if (language !== 'en') void loadStartupCatalog(language).catch(() => undefined);
}

/**
 * Start loading every language's startup catalog. The login form calls it
 * while it waits for input: the account's saved language is known only after
 * sign-in.
 */
export function preloadAllLanguageCatalogs(): void {
  for (const language of SUPPORTED_LANGUAGES) {
    if (language !== 'en') void loadStartupCatalog(language).catch(() => undefined);
  }
}
